/**
 * Tooltip — portal-based, theme-aware, keyboard- and touch-accessible.
 *
 * Use cases:
 *   - Explain abbreviated labels or icons.
 *   - Provide background on toggles + settings without filling space
 *     with help-text below every field.
 *   - Surface "why is this disabled" or "what does this number mean"
 *     hovers.
 *
 * Accessibility:
 *   - Trigger by hover OR keyboard focus (always — not opt-in).
 *   - On touch devices, taps open the tooltip; tapping the trigger
 *     again or anywhere else dismisses it.
 *   - role="tooltip" + aria-describedby wiring so screen readers
 *     announce the content when the trigger receives focus.
 *
 * Backward compatibility: pre-existing call sites use
 *   <Tooltip content="…">{trigger}</Tooltip>
 * Both `content: string` and `content: ReactNode` are accepted.
 *
 * Quality-of-life:
 *   - Optional `delay` (ms) to avoid flicker on quick mouse-over.
 *     Defaults to 150ms show, 0ms hide.
 *   - `disabled` prop to short-circuit; useful when the tooltip text
 *     is empty/conditional.
 *
 * Helpers:
 *   - <HelpTooltip content="..." /> — renders a small "?" icon with
 *     the tooltip pre-wrapped. The common "explain this field" pattern.
 */

import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

// Tooltip is rendered all over the app, including from common UI
// primitives that may be used in tests without a ThemeProvider. The
// project's `useTheme` hook THROWS if context is missing — that
// behavior is right for app code but wrong for a primitive that
// other primitives use. We catch the throw and fall back to
// light-mode defaults. This keeps tests like
// `render(<SomeComponent />)` working without each test having to
// add a ThemeProvider just because the component now wraps an icon
// button in a Tooltip.
//
// (The throw-and-recover pattern is the only safe way to do this
// without importing ThemeContext as a separate export and hitting
// circular-import/hoisting issues that surfaced in jsdom — see git
// history for the false-start.)
const useThemeSafe = () => {
  try {
    return useTheme();
  } catch {
    return { isDarkMode: false, toggleTheme: () => {}, theme: "light" as const };
  }
};

type TooltipPosition = "top" | "bottom" | "left" | "right" | "auto";

export interface TooltipProps {
  /** Tooltip body. String for backward compat; ReactNode for rich content. */
  content: React.ReactNode;
  /** Element that triggers the tooltip on hover/focus/tap. */
  children: React.ReactNode;
  /** Preferred position; `auto` picks the side with the most room. */
  position?: TooltipPosition;
  /** Wrapper className (applied to the inline-block container). */
  className?: string;
  /** Render the wrapper as a `<span>` instead of `<div>` so it can sit inline
   *  inside text without breaking layout. Defaults to `<div>`. */
  inline?: boolean;
  /** Optional show-delay in ms. Default 0 (instant) for backward compat;
   *  150-300ms is recommended for hover-only triggers to avoid flicker. */
  delay?: number;
  /** Short-circuit — if true the trigger is rendered without the tooltip. */
  disabled?: boolean;
}

const PADDING = 8;
const MOBILE_AUTO_HIDE_MS = 4000;

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = "auto",
  className = "",
  inline = false,
  delay = 0,
  disabled = false,
}) => {
  const { isDarkMode } = useThemeSafe();
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [actualPosition, setActualPosition] =
    useState<"top" | "bottom" | "left" | "right">("top");

  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  // Stable show/hide so we can cancel scheduled shows on quick leave.
  const show = useCallback(() => {
    if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    if (delay > 0) {
      showTimerRef.current = setTimeout(() => setIsVisible(true), delay);
    } else {
      setIsVisible(true);
    }
  }, [delay]);

  const hide = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setIsVisible(false);
  }, []);

  // Touch: tap to show, tap-away to hide. Also auto-hides after a few
  // seconds so the tooltip doesn't stick on mobile when the user
  // navigates away by other means.
  const toggleTouch = useCallback(() => {
    if (isVisible) {
      hide();
    } else {
      show();
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = setTimeout(
        () => setIsVisible(false),
        MOBILE_AUTO_HIDE_MS,
      );
    }
  }, [isVisible, show, hide]);

  // Cleanup pending timers on unmount.
  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    };
  }, []);

  // Position calculation runs after every show.
  useEffect(() => {
    if (!isVisible || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 80;

    let bestPosition: "top" | "bottom" | "left" | "right" =
      position === "auto" ? "top" : position;

    if (position === "auto") {
      const spaceAbove = containerRect.top;
      const spaceBelow = window.innerHeight - containerRect.bottom;
      const spaceLeft = containerRect.left;
      const spaceRight = window.innerWidth - containerRect.right;

      if (spaceAbove >= tooltipHeight + PADDING) bestPosition = "top";
      else if (spaceBelow >= tooltipHeight + PADDING) bestPosition = "bottom";
      else if (spaceRight >= tooltipWidth + PADDING) bestPosition = "right";
      else if (spaceLeft >= tooltipWidth + PADDING) bestPosition = "left";
      else bestPosition = "top";
    }

    setActualPosition(bestPosition);

    let top = 0;
    let left = 0;
    switch (bestPosition) {
      case "top":
        top = containerRect.top - PADDING;
        left = containerRect.left + containerRect.width / 2;
        break;
      case "bottom":
        top = containerRect.bottom + PADDING;
        left = containerRect.left + containerRect.width / 2;
        break;
      case "left":
        top = containerRect.top + containerRect.height / 2;
        left = containerRect.left - PADDING;
        break;
      case "right":
        top = containerRect.top + containerRect.height / 2;
        left = containerRect.right + PADDING;
        break;
    }

    const maxWidth = Math.min(400, window.innerWidth - 20);

    setTooltipStyle({
      position: "fixed",
      top: bestPosition === "top" ? undefined : top,
      bottom:
        bestPosition === "top"
          ? window.innerHeight - containerRect.top + PADDING
          : undefined,
      left: bestPosition === "left" ? undefined : left,
      right:
        bestPosition === "left"
          ? window.innerWidth - containerRect.left + PADDING
          : undefined,
      transform:
        bestPosition === "top" || bestPosition === "bottom"
          ? "translateX(-50%)"
          : "translateY(-50%)",
      maxWidth,
      zIndex: 99999,
    });
  }, [isVisible, position]);

  // Tap-outside-to-dismiss on touch devices. Skipped when hidden.
  useEffect(() => {
    if (!isVisible) return;
    const handler = (e: TouchEvent) => {
      const t = e.target as Node | null;
      if (
        containerRef.current &&
        !containerRef.current.contains(t) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(t)
      ) {
        hide();
      }
    };
    document.addEventListener("touchstart", handler);
    return () => document.removeEventListener("touchstart", handler);
  }, [isVisible, hide]);

  if (disabled || content === null || content === "" || content === undefined) {
    // Render children bare — keeps the wrapper out of layout when the
    // tooltip is conditional.
    return <>{children}</>;
  }

  const arrowClasses = (() => {
    const baseDark = "border-t-gray-700";
    const bgDark = isDarkMode ? "border-t-gray-700" : "border-t-gray-800";
    const bottomDark = isDarkMode ? "border-b-gray-700" : "border-b-gray-800";
    const leftDark = isDarkMode ? "border-l-gray-700" : "border-l-gray-800";
    const rightDark = isDarkMode ? "border-r-gray-700" : "border-r-gray-800";

    switch (actualPosition) {
      case "top":
        return `absolute -bottom-2 left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent ${bgDark}`;
      case "bottom":
        return `absolute -top-2 left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent ${bottomDark}`;
      case "left":
        return `absolute top-1/2 -right-2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent ${leftDark}`;
      case "right":
        return `absolute top-1/2 -left-2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent ${rightDark}`;
      default:
        return `absolute -bottom-2 left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent ${baseDark}`;
    }
  })();

  const tooltipContent = (
    <div
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      style={tooltipStyle}
      onMouseEnter={() => show()}
      onMouseLeave={hide}
    >
      <div
        className={`${
          isDarkMode ? "bg-gray-700 text-white" : "bg-gray-800 text-white"
        } text-sm rounded-md px-4 py-3 whitespace-normal shadow-lg leading-relaxed`}
      >
        {content}
        <div className={`w-0 h-0 border-4 ${arrowClasses}`} />
      </div>
    </div>
  );

  // Also expose `content` as a native `title=` on the wrapper when it's
  // a string. This preserves backward-compat with test queries like
  // `screen.getByTitle("Archive Camp")` that target the icon-button
  // pattern we used to write as `<button title="...">`. We suppress it
  // while the rich tooltip is visible so the user doesn't see two
  // tooltips at once.
  const nativeTitle =
    isVisible || typeof content !== "string" ? undefined : (content as string);

  // Forward direct clicks on the wrapper down to the first interactive
  // child. Without this, tests that do
  //   const el = screen.getByTitle("Archive Camp")
  //   fireEvent.click(el)
  // would click the wrapper (where the title attr lives) and the
  // <button>'s onClick handler would never fire — clicks don't
  // propagate downward.
  //
  // We only forward when the event's target IS the wrapper itself,
  // not a descendant — real user clicks on the inner button continue
  // to flow normally without double-firing.
  const forwardClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.target !== e.currentTarget) return;
    const interactive = e.currentTarget.querySelector<HTMLElement>(
      'button, a, [role="button"], input, select, textarea'
    );
    if (interactive && interactive !== e.currentTarget) {
      e.preventDefault();
      interactive.click();
    }
  };

  const handlers = {
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
    onClick: forwardClick,
    onTouchStart: toggleTouch,
    "aria-describedby": isVisible ? tooltipId : undefined,
    // Title goes on the CHILD (see decoratedChildren below), not the
    // wrapper — keeps `getByTitle("X")` returning a single match.
  };

  const wrapperClass = `${inline ? "inline" : "relative inline-block"} ${className}`;

  // Render either span or div based on inline.
  const Wrapper = inline ? "span" : "div";

  // When the child is a single React element and content is a string,
  // we also stamp `title=` on the child itself. Two reasons:
  //   1. Tests using `card.querySelector('button[title="X"]')` or
  //      `getByTitle("X")` find the actual interactive element.
  //   2. Browsers walk up the ancestor chain for native `title=`
  //      display; putting it on the closest interactive element is
  //      more accurate than the wrapper.
  // We deliberately do NOT mirror handlers onto the child — the
  // wrapper already owns hover/focus/touch, and forwardClick takes
  // care of click-through when a test clicks the wrapper directly.
  let decoratedChildren = children;
  if (
    React.isValidElement(children) &&
    typeof children !== "string" &&
    nativeTitle !== undefined
  ) {
    const childEl = children as React.ReactElement<Record<string, unknown>>;
    const childProps = childEl.props || {};
    // Don't overwrite an explicit title the consumer already set on
    // the child — respect their intent.
    if (childProps.title === undefined) {
      decoratedChildren = React.cloneElement(childEl, {
        ...childProps,
        title: nativeTitle,
      });
    }
  }

  return (
    <>
      <Wrapper
        ref={containerRef as React.RefObject<HTMLDivElement & HTMLSpanElement>}
        className={wrapperClass}
        {...handlers}
      >
        {decoratedChildren}
      </Wrapper>
      {isVisible && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default Tooltip;

/**
 * <HelpTooltip content="..." /> — convenience wrapper that renders a
 * small "?" icon with the tooltip pre-wrapped. The icon is keyboard-
 * focusable and announces the tooltip text on focus.
 *
 * Use this for the common "explain this field" pattern:
 *   <Label>Auto-cancel deadline <HelpTooltip content="..." /></Label>
 */
export interface HelpTooltipProps {
  content: React.ReactNode;
  /** Position override. */
  position?: TooltipPosition;
  /** Visual size of the help icon. Default 14px. */
  size?: number;
  /** Color class (text-*); defaults to a muted grey/blue that contrasts on both themes. */
  iconClassName?: string;
  /** Optional aria label override for screen readers. Defaults to "More info". */
  ariaLabel?: string;
}

export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  content,
  position = "auto",
  size = 14,
  iconClassName,
  ariaLabel = "More info",
}) => {
  const { isDarkMode } = useThemeSafe();
  const colorCls =
    iconClassName ||
    (isDarkMode
      ? "text-gray-400 hover:text-gray-200"
      : "text-gray-400 hover:text-gray-600");
  return (
    <Tooltip content={content} position={position} inline>
      <button
        type="button"
        aria-label={ariaLabel}
        className={`inline-flex items-center justify-center align-middle ml-1 ${colorCls} cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cc-focus)] focus-visible:rounded-full`}
        onClick={(e) => {
          // Prevent label-clicks from toggling checkboxes etc. when
          // the user just wanted to read the tooltip.
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <HelpCircle width={size} height={size} aria-hidden="true" />
      </button>
    </Tooltip>
  );
};
